/*global payhere*/
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuthContext } from "@asgardeo/auth-react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import DialogModal from "../../components/Modal/DialogModal";
import MainLayout from "../../layouts/MainLayout";
import ChatStatusBanner from "../../components/ChatPricing/ChatStatusBanner";

const PRODUCT_FEATURES = [
  {
    title: "Legal research, without query gymnastics",
    description:
      "Ask legal questions in plain English and move from issue spotting to usable analysis faster.",
  },
  {
    title: "Draft with case-law context",
    description:
      "Use the workspace to summarise material, draft content, and refine legal writing with stronger context.",
  },
  {
    title: "Built for Sri Lankan legal work",
    description:
      "A focused workspace tailored for legal professionals, not a generic public chatbot.",
  },
];

const INCLUDED_ITEMS = [
  "Monthly access to chat.paralegal.lk",
  "AI-assisted legal research workflows",
  "Drafting and summarisation workspace",
  "Ongoing platform improvements and feature updates",
];

const GROUP_PLAN_HIGHLIGHTS = [
  "Multi-user access for legal teams",
  "Coordinated onboarding support",
  "Subscription structure aligned to team usage",
];

const STATUS_OPTIONS = new Set([
  "success",
  "activating",
  "pending",
  "failed",
  "already_subscribed",
]);

const STATUS_POLL_INTERVAL_MS = 3000;
const STATUS_POLL_MAX_ATTEMPTS = 20;

const chatUrl =
  import.meta.env.VITE_CHAT_DOMAIN_URL || "https://chat.paralegal.lk";
const chatDomainLabel = chatUrl.replace(/^https?:\/\//, "").replace(/\/$/, "");
const chatDbServerUrl =
  import.meta.env.VITE_CHAT_DB_SERVER_URL || "http://localhost:8010";
const paymentsServerUrl =
  import.meta.env.VITE_PAYMENTS_SERVER_URL ||
  // import.meta.env.VITE_API_DOMAIN_URL ||
  "http://localhost:4001";

const ChatPricing = () => {
  const { state, signIn, getAccessToken, getBasicUserInfo, getDecodedIDToken } =
    useAuthContext();
  const [searchParams] = useSearchParams();
  const [modalState, setModalState] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCheckingEntitlement, setIsCheckingEntitlement] = useState(false);
  const [isPollingStatus, setIsPollingStatus] = useState(false);
  const [runtimeStatus, setRuntimeStatus] = useState(null);
  const [activationNotice, setActivationNotice] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const pollingIntervalRef = useRef(null);
  const inFlightPollRef = useRef(false);

  const statusFromQuery = useMemo(() => {
    const value = searchParams.get("status");
    return STATUS_OPTIONS.has(value) ? value : null;
  }, [searchParams]);
  const orderIdFromQuery = useMemo(() => searchParams.get("order_id"), [searchParams]);
  const status = runtimeStatus || statusFromQuery;
  const isActiveState = status === "success" || status === "already_subscribed";

  useEffect(() => {
    if (document.querySelector('script[src*="payhere.lk/lib/payhere.js"]')) {
      return;
    }

    const script = document.createElement("script");
    script.src = "https://www.payhere.lk/lib/payhere.js";
    script.defer = true;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const buildStatusUrl = (nextStatus, orderId = null) => {
    const url = new URL(window.location.href);
    url.searchParams.set("status", nextStatus);
    if (orderId) {
      url.searchParams.set("order_id", orderId);
    }
    return url.toString();
  };

  const updateStatusInUrl = useCallback((nextStatus, orderId = null) => {
    const url = new URL(window.location.href);
    url.searchParams.set("status", nextStatus);
    if (orderId) {
      url.searchParams.set("order_id", orderId);
    } else {
      url.searchParams.delete("order_id");
    }
    window.history.replaceState({}, "", url.toString());
  }, []);

  const stopStatusPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      window.clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    inFlightPollRef.current = false;
    setIsPollingStatus(false);
  }, []);

  const startStatusPolling = useCallback(
    (accessToken, orderId) => {
      if (!orderId || !accessToken) {
        return;
      }

      stopStatusPolling();
      setIsPollingStatus(true);
      setRuntimeStatus("activating");
      setActivationNotice("Payment received. Activating your chat access...");
      updateStatusInUrl("activating", orderId);

      let attempts = 0;
      pollingIntervalRef.current = window.setInterval(async () => {
        if (inFlightPollRef.current) {
          return;
        }

        inFlightPollRef.current = true;
        attempts += 1;

        try {
          const res = await fetch(
            `${chatDbServerUrl}/payment/status?order_id=${encodeURIComponent(orderId)}`,
            {
              method: "GET",
              headers: {
                Authorization: `Bearer ${accessToken}`,
              },
            }
          );

          const payload = await res.json().catch(() => ({}));

          if (res.status === 404) {
            return;
          }

          if (!res.ok) {
            throw new Error(payload?.detail || payload?.error || `HTTP ${res.status}`);
          }

          if (payload?.verified && payload?.chat_access) {
            stopStatusPolling();
            setRuntimeStatus("success");
            setActivationNotice("Subscription active. You can open chat now.");
            updateStatusInUrl("success", orderId);
            return;
          }

          if (payload?.status_code && String(payload.status_code) !== "2") {
            stopStatusPolling();
            setRuntimeStatus("failed");
            setActivationNotice("");
            updateStatusInUrl("failed", orderId);
            return;
          }
        } catch (err) {
          console.error("Subscription status poll failed:", err);
          if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
            stopStatusPolling();
            setRuntimeStatus("pending");
            setActivationNotice(
              "Payment is still being confirmed. Please refresh in a moment."
            );
            updateStatusInUrl("pending", orderId);
          }
        } finally {
          inFlightPollRef.current = false;
        }

        if (attempts >= STATUS_POLL_MAX_ATTEMPTS) {
          stopStatusPolling();
          setRuntimeStatus("pending");
          setActivationNotice(
            "Activation is taking longer than expected. We will keep your payment and complete access shortly."
          );
          updateStatusInUrl("pending", orderId);
        }
      }, STATUS_POLL_INTERVAL_MS);
    },
    [stopStatusPolling, updateStatusInUrl]
  );

  const triggerStatusPolling = useCallback(
    async (orderId) => {
      try {
        const token = await getAccessToken();
        if (!token) {
          throw new Error("Authentication token is missing.");
        }
        startStatusPolling(token, orderId);
      } catch (err) {
        console.error("Failed to start status polling:", err);
        setActivationNotice(
          "Payment completed. We could not auto-refresh access status. Please refresh this page."
        );
      }
    },
    [getAccessToken, startStatusPolling]
  );

  const startPaymentWithCallbacks = (paymentPayload, orderId) => {
    payhere.onCompleted = function onCompleted() {
      setIsProcessing(false);
      setRuntimeStatus("pending");
      updateStatusInUrl("pending", orderId);
      triggerStatusPolling(orderId);
    };

    payhere.onDismissed = function onDismissed() {
      setIsProcessing(false);
    };

    payhere.onError = function onError(error) {
      console.error("PayHere payment error:", error);
      setIsProcessing(false);
      setRuntimeStatus("failed");
      updateStatusInUrl("failed", orderId);
      setErrorMessage("Payment failed. Please try again.");
      setModalState("payment_error");
    };

    payhere.startPayment(paymentPayload);
  };

  const requestPaymentHash = async (accessToken, orderId, amount, currency, email) => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
    };

    const makeRequest = async (requestOrderId) => {
      const res = await fetch(`${paymentsServerUrl}/payment/start`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          order_id: requestOrderId,
          amount,
          currency,
        }),
      });

      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        const message = body.error || body.detail || `HTTP ${res.status}`;
        const err = new Error(message);
        err.status = res.status;
        throw err;
      }
      return { ...body, order_id_used: requestOrderId };
    };

    try {
      return await makeRequest(orderId);
    } catch (err) {
      // Compatibility fallback for current payment gateway behavior.
      if (
        err?.status === 403 &&
        String(err.message || "").toLowerCase().includes("order id must match your email")
      ) {
        return await makeRequest(email);
      }
      throw err;
    }
  };

  const handleStartSubscription = async () => {
    if (isProcessing || isPollingStatus || isActiveState) {
      return;
    }

    if (!state?.isAuthenticated) {
      setModalState("auth_required");
      return;
    }

    if (typeof payhere === "undefined") {
      setErrorMessage("Payment script is still loading. Please try again in a moment.");
      setModalState("payment_error");
      return;
    }

    setIsProcessing(true);
    setErrorMessage("");
    setActivationNotice("");

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error("Authentication token is missing.");
      }

      const [basicUserInfo, decodedToken] = await Promise.all([
        getBasicUserInfo(),
        getDecodedIDToken(),
      ]);

      const email = basicUserInfo?.email || decodedToken?.email;
      if (!email) {
        throw new Error("Unable to resolve user email from your account.");
      }

      const entitlementResponse = await fetch(
        `${chatDbServerUrl}/chat-entitlements/me`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      const entitlementData = await entitlementResponse.json().catch(() => ({}));
      if (entitlementResponse.ok && entitlementData?.chat_access) {
        setIsProcessing(false);
        setRuntimeStatus("already_subscribed");
        updateStatusInUrl("already_subscribed");
        setActivationNotice("This account already has active chat access.");
        return;
      }

      const checkoutResponse = await fetch(
        `${chatDbServerUrl}/chat-subscriptions/checkout/start`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            return_url: buildStatusUrl("pending"),
            cancel_url: buildStatusUrl("failed"),
          }),
        }
      );

      const checkoutData = await checkoutResponse.json().catch(() => ({}));
      if (!checkoutResponse.ok) {
        throw new Error(
          checkoutData?.detail || "Failed to create subscription checkout session."
        );
      }

      const formattedAmount = Number(checkoutData.amount).toFixed(2);
      const orderId = checkoutData.order_id;
      const currency = checkoutData.currency || "LKR";

      const hashData = await requestPaymentHash(
        accessToken,
        orderId,
        formattedAmount,
        currency,
        email
      );

      if (!hashData?.hash || !hashData?.merchant_id) {
        throw new Error("Payment hash generation failed.");
      }

      if (!import.meta.env.VITE_NOTIFY_URL) {
        throw new Error("Payment notify URL is not configured.");
      }

      const paymentPayload = {
        sandbox: import.meta.env.VITE_SANDBOX !== "false",
        merchant_id: hashData.merchant_id,
        return_url: buildStatusUrl("pending"),
        cancel_url: buildStatusUrl("failed"),
        notify_url: import.meta.env.VITE_NOTIFY_URL,
        order_id: hashData.order_id_used || orderId,
        items: `${checkoutData.plan_name || "Chat Pro"} Subscription`,
        amount: formattedAmount,
        currency,
        first_name: basicUserInfo?.givenName || decodedToken?.given_name || "",
        last_name: basicUserInfo?.familyName || decodedToken?.family_name || "",
        email,
        phone: "",
        address: "",
        city: "",
        country: "Sri Lanka",
        hash: hashData.hash,
        custom_1: "individual",
        custom_2: checkoutData.plan_code || "CHAT_PRO_MONTHLY",
      };

      startPaymentWithCallbacks(paymentPayload, paymentPayload.order_id);
    } catch (error) {
      console.error("Subscription checkout error:", error);
      setErrorMessage(error.message || "Failed to initialize payment.");
      setModalState("payment_error");
      setIsProcessing(false);
    }
  };

  const handleLogin = () => {
    sessionStorage.setItem(
      "redirectAfterLogin",
      `${window.location.pathname}${window.location.search}`
    );
    signIn();
  };

  useEffect(() => {
    let cancelled = false;

    const checkEntitlementOnLoad = async () => {
      if (!state?.isAuthenticated) {
        setIsCheckingEntitlement(false);
        return;
      }

      setIsCheckingEntitlement(true);
      try {
        const accessToken = await getAccessToken();
        if (!accessToken || cancelled) {
          return;
        }

        const res = await fetch(`${chatDbServerUrl}/chat-entitlements/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        const payload = await res.json().catch(() => ({}));

        if (cancelled || !res.ok) {
          return;
        }

        if (payload?.chat_access) {
          stopStatusPolling();
          setRuntimeStatus("already_subscribed");
          updateStatusInUrl("already_subscribed");
          setActivationNotice("This account already has active chat access.");
        }
      } catch (error) {
        console.error("Initial entitlement check failed:", error);
      } finally {
        if (!cancelled) {
          setIsCheckingEntitlement(false);
        }
      }
    };

    checkEntitlementOnLoad();

    return () => {
      cancelled = true;
    };
  }, [state?.isAuthenticated, getAccessToken, stopStatusPolling, updateStatusInUrl]);

  useEffect(() => {
    return () => {
      stopStatusPolling();
    };
  }, [stopStatusPolling]);

  useEffect(() => {
    if (!state?.isAuthenticated || !orderIdFromQuery) {
      return;
    }

    if (statusFromQuery !== "pending" && statusFromQuery !== "activating") {
      return;
    }

    triggerStatusPolling(orderIdFromQuery);
  }, [state?.isAuthenticated, orderIdFromQuery, statusFromQuery, triggerStatusPolling]);

  const startButtonDisabled =
    isCheckingEntitlement || isProcessing || isPollingStatus || isActiveState;
  const startButtonLabel = isCheckingEntitlement
    ? "Checking Access..."
    : isActiveState
    ? "Subscription Active"
    : isPollingStatus || status === "activating"
    ? "Activating Access..."
    : isProcessing
    ? "Processing..."
    : "Start Subscription";

  const modalConfig =
    modalState === "auth_required"
      ? {
          title: "Login required",
          description: "Please log in to continue to checkout.",
          actions: [
            { label: "Close", onClick: () => setModalState(null) },
            { label: "Log in", onClick: handleLogin, variant: "primary" },
          ],
        }
      : modalState === "payment_error"
      ? {
          title: "Payment initialization failed",
          description: errorMessage || "Unable to start payment. Please try again.",
          actions: [
            { label: "Close", onClick: () => setModalState(null), variant: "primary" },
          ],
        }
      : null;

  return (
    <MainLayout>
      <Helmet>
        <title>Chat | paralegal.lk</title>
        <meta
          name="description"
          content="Subscribe to Paralegal’s dedicated chat workspace for AI-assisted legal research, drafting, and analysis."
        />
        <link rel="canonical" href="https://www.paralegal.lk/chat" />
      </Helmet>

      <div className="w-full max-w-6xl px-6 py-10 text-pl-text-primary md:px-10 md:py-14">
        <ChatStatusBanner status={status} />

        <section className="rounded-[24px] border border-pl-border bg-pl-bg-elevated p-8 shadow-pl-md md:p-12">
          <div className="grid gap-10 lg:grid-cols-[1.2fr_0.8fr] lg:items-start">
            <div>
              <p className="text-12 font-semibold uppercase tracking-[0.22em] text-pl-accent">
                Subscription Workspace for Legal Professionals
              </p>
              <h1 className="mt-4 max-w-3xl font-display text-36 font-semibold leading-tight md:text-[52px]">
                AI legal research and drafting for Sri Lankan legal practice.
              </h1>
              <p className="mt-5 max-w-2xl text-16 leading-8 text-pl-text-secondary md:text-18">
                <a
                  href={chatUrl}
                  className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
                >
                  {chatDomainLabel}
                </a>{" "}
                is Paralegal’s dedicated subscription workspace for deeper
                legal research, drafting, and analysis. Work faster from first
                question to final written output in one focused environment.
              </p>

              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <button
                  type="button"
                  onClick={handleStartSubscription}
                  disabled={startButtonDisabled}
                  className={`rounded-pl px-6 py-3 text-15 font-semibold text-white transition-colors ${
                    startButtonDisabled
                      ? "cursor-not-allowed bg-pl-text-tertiary"
                      : "bg-pl-accent hover:bg-pl-accent-hover"
                  }`}
                  aria-label="Start monthly chat subscription"
                >
                  {startButtonLabel}
                </button>
                {isActiveState && (
                  <a
                    href={chatUrl}
                    className="inline-flex items-center justify-center rounded-pl border border-pl-accent bg-transparent px-6 py-3 text-15 font-semibold text-pl-accent transition-colors duration-150 hover:bg-pl-accent hover:text-white"
                  >
                    Open Chat
                  </a>
                )}
                <Link
                  to="/about"
                  className="rounded-pl border border-pl-border bg-pl-bg px-6 py-3 text-15 font-semibold text-pl-text-primary transition-colors hover:bg-pl-bg-subtle"
                >
                  Learn about Paralegal
                </Link>
              </div>

              {activationNotice && (
                <p className="mt-3 text-14 leading-7 text-pl-text-secondary">
                  {activationNotice}
                </p>
              )}

              <p className="mt-4 text-14 font-semibold text-pl-text-primary">
                Access is available to registered subscribers only.
              </p>
              <p className="mt-2 max-w-2xl text-14 leading-7 text-pl-text-secondary">
                Sign in and subscribe to access{" "}
                <a
                  href={chatUrl}
                  className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
                >
                  {chatDomainLabel}
                </a>
                . Already subscribed? Sign in with your Paralegal account.
              </p>
              <p className="mt-4 text-13 text-pl-text-tertiary">
                Checkout is handled securely through PayHere and subscription
                access is enabled after payment confirmation.
              </p>
            </div>

            <aside className="rounded-[20px] border border-pl-border bg-pl-bg-subtle p-6 shadow-pl-sm">
              <p className="text-12 font-semibold uppercase tracking-[0.18em] text-pl-accent">
                Monthly Plan
              </p>
              <h2 className="mt-3 font-display text-24 font-semibold text-pl-text-primary">
                Paralegal Chat Pro
              </h2>
              <div className="mt-4 flex items-end gap-2">
                <span className="font-display text-36 font-semibold">Rs. 20,000</span>
                <span className="pb-1 text-15 text-pl-text-secondary">/ month</span>
              </div>
              <p className="mt-4 text-14 leading-7 text-pl-text-secondary">
                One straightforward subscription tier designed for legal
                professionals and legal teams.
              </p>

              <ul className="mt-6 space-y-3">
                {INCLUDED_ITEMS.map((item) => (
                  <li key={item} className="flex gap-3 text-14 leading-6 text-pl-text-primary">
                    <span
                      className="mt-[6px] h-2.5 w-2.5 rounded-full bg-pl-accent"
                      aria-hidden="true"
                    />
                    <span>
                      {item === "Monthly access to chat.paralegal.lk" ? (
                        <>
                          Monthly access to{" "}
                          <a
                            href={chatUrl}
                            className="text-pl-accent underline underline-offset-4 hover:text-pl-accent-hover"
                          >
                            {chatDomainLabel}
                          </a>
                        </>
                      ) : (
                        item
                      )}
                    </span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={handleStartSubscription}
                disabled={startButtonDisabled}
                className={`mt-8 w-full rounded-pl px-6 py-3 text-15 font-semibold text-white transition-colors ${
                  startButtonDisabled
                    ? "cursor-not-allowed bg-pl-text-tertiary"
                    : "bg-pl-accent hover:bg-pl-accent-hover"
                }`}
              >
                {startButtonLabel}
              </button>

              <p className="mt-4 text-12 leading-6 text-pl-text-tertiary">
                By continuing, you agree to our{" "}
                <Link to="/terms" className="text-pl-accent hover:underline">
                  Terms
                </Link>
                ,{" "}
                <Link to="/privacy" className="text-pl-accent hover:underline">
                  Privacy Policy
                </Link>
                , and{" "}
                <Link to="/refund" className="text-pl-accent hover:underline">
                  Refund Policy
                </Link>
                .
              </p>
            </aside>
          </div>
        </section>

        <section className="mt-8 rounded-[24px] border border-pl-border bg-pl-bg-elevated p-8 shadow-pl-md md:p-12">
          <div className="grid gap-8 lg:grid-cols-[1fr_0.9fr] lg:items-start">
            <div>
              <h2 className="font-display text-28 font-semibold text-pl-text-primary md:text-36">
                Need access for your firm or team?
              </h2>
              <p className="mt-4 max-w-3xl text-16 leading-8 text-pl-text-secondary">
                For chambers, law firms, and legal teams that need multi-user
                access, shared onboarding, and tailored support, choose a group
                subscription.
              </p>
            </div>

            <aside className="rounded-[20px] border border-pl-border bg-pl-bg-subtle p-6 shadow-pl-sm">
              <h3 className="font-display text-24 font-semibold text-pl-text-primary">
                Group Subscription
              </h3>
              <p className="mt-4 text-14 leading-7 text-pl-text-secondary">
                Contact us for team pricing and onboarding tailored to your
                organization.
              </p>

              <ul className="mt-6 space-y-3">
                {GROUP_PLAN_HIGHLIGHTS.map((item) => (
                  <li key={item} className="flex gap-3 text-14 leading-6 text-pl-text-primary">
                    <span
                      className="mt-[6px] h-2.5 w-2.5 rounded-full bg-pl-accent"
                      aria-hidden="true"
                    />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <a
                href="mailto:admin@paralegal.lk?subject=Group%20Subscription%20Inquiry"
                className="mt-8 inline-flex w-full items-center justify-center rounded-pl border border-pl-accent bg-pl-bg-elevated px-6 py-3 text-15 font-semibold text-pl-accent transition-colors hover:bg-pl-accent-subtle"
              >
                Contact Us
              </a>

              <p className="mt-4 text-12 leading-6 text-pl-text-tertiary">
                Prefer to discuss requirements first? Contact us for a tailored
                plan.
              </p>
            </aside>
          </div>
        </section>

        <section className="mt-8 grid gap-5 md:grid-cols-3">
          {PRODUCT_FEATURES.map((feature) => (
            <article
              key={feature.title}
              className="rounded-[20px] border border-pl-border bg-pl-bg-elevated p-6 shadow-pl-sm"
            >
              <h2 className="font-display text-24 font-semibold text-pl-text-primary">
                {feature.title}
              </h2>
              <p className="mt-3 text-15 leading-7 text-pl-text-secondary">
                {feature.description}
              </p>
            </article>
          ))}
        </section>
      </div>

      <DialogModal
        isOpen={Boolean(modalConfig)}
        onClose={() => setModalState(null)}
        title={modalConfig?.title}
        description={modalConfig?.description}
        actions={modalConfig?.actions}
      />
    </MainLayout>
  );
};

export default ChatPricing;
